import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Alert, Button, Card, Spinner, TextField } from '@skupreme/shared/ui';
import { enrollMfa, signInWithGoogle, verifyMfa } from '../api/auth';
import type { GoogleSignInResponse } from '../api/types';
import { GoogleButton } from '../auth/GoogleButton';
import { startSession } from '../auth/session';
import { useSignedIn } from '../auth/useSession';

const CODE_PATTERN = /^\d{6}$/;

function MfaStep({ pending }: { pending: GoogleSignInResponse }) {
  const token = pending.mfa_pending_token;
  const [code, setCode] = useState('');
  const [enrolCode, setEnrolCode] = useState('');

  // First login: the one-time enrolment code from the admin who granted access
  // opens the authenticator setup. Submitted by hand, so it runs once.
  const enrollment = useMutation({
    mutationFn: (value: string) => enrollMfa(token, value),
  });
  const verify = useMutation({
    mutationFn: (value: string) => verifyMfa(token, value),
    onSuccess: startSession,
  });

  const digits = code.replace(/\s/g, '');
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (CODE_PATTERN.test(digits)) {
      verify.mutate(digits);
    }
  };

  if (!pending.mfa_enrolled && !enrollment.data) {
    const enrol = (event: FormEvent) => {
      event.preventDefault();
      if (enrolCode.trim()) {
        enrollment.mutate(enrolCode.trim());
      }
    };
    return (
      <form onSubmit={enrol} className="sk:flex sk:flex-col sk:gap-3">
        <p>
          First sign-in: enter the one-time enrolment code you were given to set
          up your authenticator app.
        </p>
        <TextField
          label="Enrolment code"
          value={enrolCode}
          onChange={setEnrolCode}
          autoComplete="off"
        />
        {enrollment.isError && (
          <Alert tone="danger">{enrollment.error.message}</Alert>
        )}
        {enrollment.isPending && (
          <Spinner label="Preparing authenticator setup" />
        )}
        <Button
          type="submit"
          variant="primary"
          isDisabled={!enrolCode.trim() || enrollment.isPending}
        >
          Continue
        </Button>
      </form>
    );
  }
  return (
    <form onSubmit={submit} className="sk:flex sk:flex-col sk:gap-3">
      {enrollment.data && (
        <>
          <p>
            Scan this code with your authenticator app, then enter the 6-digit
            code it shows.
          </p>
          <QRCodeSVG
            value={enrollment.data.otpauth_uri}
            size={176}
            title="Authenticator setup code"
          />
        </>
      )}
      {pending.mfa_enrolled && (
        <p>Enter the 6-digit code from your authenticator app.</p>
      )}
      <TextField
        label="Authenticator code"
        placeholder="123456"
        value={code}
        onChange={setCode}
        inputMode="numeric"
        autoComplete="one-time-code"
      />
      {verify.isError && <Alert tone="danger">{verify.error.message}</Alert>}
      <Button
        type="submit"
        variant="primary"
        isDisabled={!CODE_PATTERN.test(digits) || verify.isPending}
      >
        Verify
      </Button>
    </form>
  );
}

export function LoginPage() {
  const signedIn = useSignedIn();
  const [pending, setPending] = useState<GoogleSignInResponse | null>(null);
  const google = useMutation({
    mutationFn: signInWithGoogle,
    onSuccess: setPending,
  });

  if (signedIn) {
    return <Navigate to="/" replace />;
  }
  return (
    <div className="sk:mx-auto sk:mt-24 sk:max-w-sm">
      <Card title="Skupreme Staff">
        <div className="sk:flex sk:flex-col sk:gap-3">
          {pending ? (
            <MfaStep pending={pending} />
          ) : (
            <>
              <GoogleButton onCredential={idToken => google.mutate(idToken)} />
              {google.isPending && <Spinner label="Signing in" />}
              {google.isError && (
                <Alert tone="danger">{google.error.message}</Alert>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
